// Inject thin scrollbar styles for navigation/sidebar (cross-browser)
if (typeof window !== 'undefined' && typeof document !== 'undefined' && !document.getElementById('nav-scrollbar-style')) {
    const style = document.createElement('style');
    style.id = 'nav-scrollbar-style';
    style.innerHTML = `
        #scrollbar, .sidebar-nav {
            scrollbar-width: thin; /* Firefox */
            scrollbar-color: #bdbdbd #f5f5f5; /* Firefox */
        }
        #scrollbar::-webkit-scrollbar, .sidebar-nav::-webkit-scrollbar {
            width: 6px;
            background: #f5f5f5;
        }
        #scrollbar::-webkit-scrollbar-thumb, .sidebar-nav::-webkit-scrollbar-thumb {
            background: #bdbdbd;
            border-radius: 4px;
        }
    `;
    document.head.appendChild(style);
}
/**
 * Navigation Rendering Module - Application Routes and Menu Rendering
 * 
 * Provides three main exports:
 * 1. RenderRoutes - React Router configuration with access control
 * 2. RenderMenuLeft - Left sidebar menu with navigation and user info
 * 3. Supporting functions for menu interactions and token refresh
 * 
 * Features:
 * - Dynamic route rendering based on authentication state
 * - Protected/private route enforcement
 * - Left sidebar navigation with collapsible admin menu
 * - About dialog with deployment version information
 * - Automatic token refresh every 15 minutes (900,000ms)
 * - Client-specific menu items (generic vs contract)
 * - Active route highlighting
 * - Menu size toggle (expand/collapse sidebar)
 * 
 * Client Types Supported:
 * - "generic": Generic document management client
 * - other: Contract/-specific client with different admin items
 * 
 * Authentication:
 * - Uses AuthData context from AuthWrapper for user state
 * - Token refresh runs on component mount and every 15 minutes
 * - Logout available in left menu footer
 * 
 * Dependencies:
 * - React: UI library with hooks (useState, useEffect)
 * - react-router-dom: Route, Routes, Link, useLocation
 * - AuthWrapper: User authentication context
 * - config_mapping: Configuration constants and styling
 * - server_utils: API communication utilities
 * - admin services: Deployment info API
 * 
 * @module rendernavigation
 * @exports {JSX.Element} RenderRoutes - Routes configuration component
 * @exports {JSX.Element} RenderMenuLeft - Left sidebar menu component
 */

import React, { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { AuthData } from "../../auth/AuthWrapper";
import CommonIcon from '../common/customicon';
import { nav } from "./navigation";
import { API_SERVER_URL } from "../../utils/config_ip_details";
import { API_SUCCESS_RESPONSE_CODE, PRODUCT_NAME, AUTH_TOKEN_REFRESH_DURATION } from "../../utils/config_mapping";
import { apiCall } from "../../utils/server_utils";
import { trackError } from "../../utils/error_tracker";
import { getUsername } from "../../utils/common_functions";
import { Link,useLocation} from 'react-router-dom'; // Import Link from react-router-dom
import { getDeploymentInfo} from '../../services/admin'; 

const app_client = process.env.REACT_APP_CLIENT;

/**
 * RenderRoutes Component
 * 
 * Dynamically renders all application routes from the navigation configuration
 * Applies authentication-based access control to private routes
 * 
 * Route Rendering Logic:
 * 1. If route.isPrivate === true AND user is authenticated: render route
 * 2. If route.isPrivate === false: render route (public)
 * 3. Otherwise: don't render (private route without authentication)
 * 
 * Private Routes:
 * - Only rendered if user.isAuthenticated === true
 * - User authentication from AuthData context (AuthWrapper)
 * - Unauthenticated users cannot access these routes
 * 
 * Public Routes:
 * - Always rendered regardless of authentication state
 * - Includes login, about, and other public pages
 * 
 * @component
 * @returns {JSX.Element} React Router Routes container with conditional route rendering
 * 
 * @example
 * // Usage in App.js
 * <RenderRoutes />
 * 
 * @note Routes from navigation.js are mapped here with access control
 * @note Empty return (false) for unauthorized private routes prevents rendering
 */
export const RenderRoutes = () => {
    const { user } = AuthData();
    return (
        <Routes>
            {nav.map((r, i) => {
                // Check access permission for this route
                if (r.isPrivate && user.isAuthenticated) {
                    // Private route AND user is authenticated: render it
                    return <Route key={i} path={r.path} element={r.element} />;
                } else if (!r.isPrivate) {
                    // Public route: always render
                    return <Route key={i} path={r.path} element={r.element} />;
                } else return false;
                // Private route WITHOUT authentication: don't render
            })}
        </Routes>
    );
};

/**
 * RenderMenuLeft Component
 * 
 * Renders the left sidebar menu with application navigation
 * Includes about dialog, admin dropdown, menu toggle, and logout functionality
 * 
 * State Management:
 * - isMenuSmall: Tracks if sidebar is collapsed (true = collapsed)
 * - isAdminDropdownOpen: Tracks if Admin dropdown is visible
 * - isDataExplorerDropdownOpen: Tracks if Data Explorer dropdown is visible (currently not used)
 * - isAboutDialogOpen: Tracks if About modal is displayed
 * - deploymentInfo: Version and deployment date (fetched from API)
 * 
 * Features:
 * - Responsive left sidebar with product branding
 * - Main menu items (Document List, Data Explorer, AskGenie, etc.)
 * - Collapsible Admin dropdown with conditional items
 * - About modal with version information
 * - Auto-refresh auth token every 15 minutes
 * - Active route highlighting
 * - Menu size toggle (expand/collapse)
 * - User info display and logout button
 * 
 * Client-Specific Menus:
 * - Generic client: Shows Clauses, Taxonomies, Connectors, Notifications
 * -  client: Shows Taxonomy Management, Confidence Settings
 * - Contract client: Limited admin items
 * 
 * Token Refresh:
 * - Runs on component mount via useEffect
 * - Repeats every AUTH_TOKEN_REFRESH_DURATION (900,000ms = 15 minutes)
 * - Updates localStorage with new access token if valid
 * - Logs out user if refresh token has expired
 * 
 * @component
 * @returns {JSX.Element} Sidebar navigation with menus, modals, and user controls
 * 
 * @example
 * // Usage in App layout
 * <RenderMenuLeft />
 */
export const RenderMenuLeft = () => {
    const { user, isLoggedIn, logout } = AuthData();
    // ===== STATE MANAGEMENT =====
    const [isMenuSmall, setIsMenuSmall] = useState(false); // Tracks menu size (false = expanded, true = small/collapsed)
    const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false); // Tracks Admin dropdown visibility
    const [isDashboardDropdownOpen, setIsDashboardDropdownOpen] = useState(false); // Tracks Dashboard dropdown visibility
    const [isDataExplorerDropdownOpen, setIsDataExplorerDropdownOpen] = useState(false); // Tracks Data Explorer dropdown (currently not used)
    const [isAboutDialogOpen, setIsAboutDialogOpen] = useState(false); // Controls About modal visibility
    const [deploymentInfo, setDeploymentInfo] = useState({ version: '2.0.1', deploymentDate: '14-Nov-2025' }); // Version and deployment date
    const location = useLocation(); // Get the current URL location for active route highlighting

    /**
     * Fetch deployment information from API
     * Called on component mount
     * Updates version and deployment date in About modal
     * 
     * @async
     * @function fetchDeploymentInfo
     * @returns {void}
     * 
     * API Endpoint: GET /api/deployment-info
     * Response Shape: { success: boolean, data: { version: string, deploymentDate: string } }
     * 
     * Fallback: Uses default values if API fails
     */
    useEffect(() => {
        const fetchDeploymentInfo = async () => {
            try {
                const response = await getDeploymentInfo();
                // Check if response is valid with data
                if (response && response.success && response.data) {
                    setDeploymentInfo({
                        version: response.data.version || '2.0.1',
                        deploymentDate: response.data.deploymentDate || '14-Nov-2025'
                    });
                }
            } catch (error) {
                console.error('Error fetching deployment info:', error);
                // Keep default values if API call fails - allows graceful degradation
            }
        };
        
        fetchDeploymentInfo();
    }, []);

    /**
     * Toggle About modal visibility
     * Prevents default link behavior and toggles modal state
     * 
     * @function toggleAboutDialog
     * @param {Event} e - Click event object
     * @returns {void}
     */
    const toggleAboutDialog = (e) => {
        e.preventDefault();
        setIsAboutDialogOpen(!isAboutDialogOpen);
    };

    /**
     * Toggle sidebar size between expanded (lg) and collapsed (sm)
     * Updates HTML data attribute to notify CSS framework
     * Syncs React state with DOM changes
     * 
     * @function toggleMenu
     * @returns {void}
     * 
     * Process:
     * 1. Get current data-sidebar-size attribute from <html> tag
     * 2. If 'sm' (small), change to 'lg' (large)
     * 3. If 'lg' (large), change to 'sm' (small)
     * 4. Update state to trigger re-render
     * 
     * CSS Framework Integration:
     * - CSS watches data-sidebar-size attribute
     * - Changes sidebar width and menu styling accordingly
     */
    const toggleMenu = () => {
        const htmlElement = document.documentElement; // Refers to the <html> tag
        const currentMenuStatus = htmlElement.getAttribute('data-sidebar-size');
        const menuStatus = currentMenuStatus === 'sm' ? 'lg' : 'sm'; // Toggle between 'sm' and 'lg'
        htmlElement.setAttribute('data-sidebar-size', menuStatus);
        setIsMenuSmall(!isMenuSmall); // Update the state to trigger React re-render
    };

    /**
     * Toggle Admin dropdown menu visibility
     * Shows/hides admin menu items
     * 
     * @function toggleAdminDropdown
     * @returns {void}
     * 
     * Admin menu items shown based on client type:
     * - Generic: Clauses, Taxonomies, Connectors, Notifications
     * - : Taxonomy Management, Confidence Settings
     * - Contract: No items (dropdown still toggles)
     */
    const toggleAdminDropdown = () => {
        setIsAdminDropdownOpen(!isAdminDropdownOpen);
    };

    /**
     * Toggle Dashboard dropdown menu visibility
     * Shows/hides dashboard menu items
     *
     * @function toggleDashboardDropdown
     * @returns {void}
     */
    const toggleDashboardDropdown = () => {
        setIsDashboardDropdownOpen(!isDashboardDropdownOpen);
    };

    /**
     * Toggle Data Explorer dropdown menu visibility
     * Currently not used in the UI (commented out)
     * Could be used to expand data explorer options
     * 
     * @function toggleDataExplorerDropdown
     * @returns {void}
     */
    const toggleDataExplorerDropdown = () => {
        setIsDataExplorerDropdownOpen(!isDataExplorerDropdownOpen);
    };

    /**
     * Refresh the authentication access token
     * Called periodically to maintain active session
     * 
     * @async
     * @function refreshAuthToken
     * @returns {Promise<void>}
     * 
     * Process:
     * 1. Call refresh-access-token API endpoint
     * 2. Check for API success response code
     * 3. If refresh token expired: logout user
     * 4. If new token received: update localStorage
     * 5. If API error: log error without failing
     * 
     * Token Storage:
     * - Stored in localStorage.jsondata as JSON
     * - accessToken field holds the actual token
     * - Automatically sent with API requests
     * 
     * Error Handling:
     * - Catches and logs errors without throwing
     * - Allows app to continue even if token refresh fails
     * - Logs API errors via trackError function
     * 
     * @example
     * // Called automatically on mount and every 15 minutes
     * // No manual invocation needed
     */
    const refreshAuthToken = async () => {
        let apiUrl = API_SERVER_URL + 'refresh-access-token/';
        let method = 'POST';
        try {
            const result = await apiCall(method, apiUrl);
            if (result !== undefined) {
                // Check for successful API response
                if (result.code === API_SUCCESS_RESPONSE_CODE) {
                    // Check if refresh token has expired
                    if (result.data.error === "Refresh token expired") {
                        logout(); // Force logout if refresh token expired
                        return;
                    }
                    
                    // Update access token in localStorage
                    let jsondata = localStorage.getItem('jsondata');
                    if (jsondata !== undefined && jsondata !== null) {
                        const jsonData = JSON.parse(jsondata);
                        if (result.data.access_token !== undefined) {
                            jsonData.accessToken = result.data.access_token; // Update token
                            localStorage.setItem('jsondata', JSON.stringify(jsonData));
                        }
                    }
                } else {
                    // Log API error for debugging
                    trackError(
                        'The API response is invalid:<br/>URL:' +
                        apiUrl +
                        '<br/>Method:' +
                        method +
                        '<br>',
                        JSON.stringify(result)
                    );
                }
            }
        } catch (error) {
            console.error('Error refreshing token:', error);
        }
    };

    /**
     * Setup token refresh on component mount and interval
     * 
     * @useEffect
     * @function setupTokenRefresh
     * @returns {Function} Cleanup function to clear interval
     * 
     * Process:
     * 1. Call refreshAuthToken immediately on mount
     * 2. Set up interval to refresh every AUTH_TOKEN_REFRESH_DURATION (900,000ms = 15 minutes)
     * 3. Return cleanup function to clear interval on unmount
     * 
     * Cleanup:
     * - Clears the interval when component unmounts
     * - Prevents memory leaks from orphaned intervals
     * - Stops token refresh attempts if component is destroyed
     */
    useEffect(() => {
        refreshAuthToken(); // Refresh immediately on mount
        const interval = setInterval(refreshAuthToken, AUTH_TOKEN_REFRESH_DURATION); // Refresh every 15 minutes
        return () => clearInterval(interval); // Cleanup: clear interval on unmount
    }, []); // Empty dependency array ensures this effect runs once on mount

    // Get username from storage for display in menu footer
    const logged_user_name = getUsername();
    
    return (
        <>

        {/* ===== ABOUT MODAL DIALOG ===== */}
        {/* About modal showing version and deployment info */}
        {isAboutDialogOpen && (
                        <div 
  className="modal fade show" 
  style={{ display: "block", backgroundColor: "rgba(0,0,0,0.7)" }}
  onClick={toggleAboutDialog}
>
  {/* Modal dialog centered on screen */}
  <div 
    className="modal-dialog modal-dialog-centered" 
    onClick={(e) => e.stopPropagation()}
  >
    {/* Modal content with rounded corners and shadow */}
    <div className="modal-content shadow-lg border-0" style={{ borderRadius: "16px" }}>
      
      <div className="modal-body text-center p-4">

        {/* Product Logo */}
       <div className="mb-3 text-center">
  <div style={{ backgroundColor: "#161950", display: "inline-block", padding: "5px 10px", borderRadius: "6px" }}>
    <img
      src="../../../images/predatica_logo.png"
      alt="Logo"
      height="30px"
    />
  </div>
</div>

        {/* Product Name with HTML support */}
        <h3 
          className="fw-bold mb-1"
          dangerouslySetInnerHTML={{ __html: PRODUCT_NAME }}
        ></h3>

        {/* Version Information - Dynamically loaded from API */}
        <p className="mt-3 mb-1">
          <strong>Version:</strong> {deploymentInfo.version}
        </p>
        <p className="mb-3">
          <strong>Date of the version:</strong> {deploymentInfo.deploymentDate}
        </p>

        <hr />

        {/* Copyright/Footer */}
        <p className="text-muted small mb-0">
          © 2025 Contact Genie. All rights reserved.
        </p>

      </div>

      {/* Modal Footer with Close Button */}
      <div className="modal-footer border-0 justify-content-center pb-4">
        <button type="button" className="btn btn-dark px-4" onClick={toggleAboutDialog}>
          Close
        </button>
      </div>

    </div>
  </div>
</div>

                    )}

            {/* ===== LEFT SIDEBAR MENU ===== */}
            <div className="app-menu navbar-menu">
                {/* Brand/Logo Section */}
                <div className="navbar-brand-box">
                    <Link to="/taxonomy-management" className="logo logo-dark">
                        {/* Small logo (mobile view) */}
                        {/* <span className="logo-sm">
                            <CommonIcon iconName="FileText" color="#fff" />
                            {!isMenuSmall && (
                                <span className="text-primary mb-3" dangerouslySetInnerHTML={{ __html: PRODUCT_NAME }} />
                            )}
                        </span> */}
                        {/* Large logo (desktop view) */}
                        <span className="logo-lg">
                            <CommonIcon iconName="FileText" color="#fff" />
                            {!isMenuSmall && (
                                <>
                                    <span className="text-primary mb-3" dangerouslySetInnerHTML={{ __html: PRODUCT_NAME }} />
                                    <sup>TM</sup>
                                </>
                            )}
                        </span>
                    </Link>
                    <Link to="/taxonomy-management" className="logo logo-light">
                        {/* <span className="logo-sm">
                            <CommonIcon iconName="FileText" color="#fff" />
                            {!isMenuSmall && (
                                <span className="text-primary mb-3" dangerouslySetInnerHTML={{ __html: PRODUCT_NAME }} />
                            )}
                        </span> */}
                        <span className="logo-lg">
                            <CommonIcon iconName="FileText" color="#fff" />
                            {!isMenuSmall && (
                                <span dangerouslySetInnerHTML={{ __html: PRODUCT_NAME }} />
                            )}
                        </span>
                    </Link>
                    {/* Menu Toggle Button */}
                    <a href="javascript:void(0)" onClick={toggleMenu} id="menuclick">
                        <i className={`menu-icon text-right`}>
                            {/* Icon changes based on menu state (collapse/expand) */}
                            {isMenuSmall ? (
                                <CommonIcon iconName="ChevronRight" size={"17px"} color="#fff" /> // Show right arrow when collapsed
                            ) : (
                                <CommonIcon iconName="ChevronLeft" size={"17px"} color="#fff" /> // Show left arrow when expanded
                            )}
                        </i>
                    </a>
                </div>

                {/* Scrollable Menu Content */}
                <div id="scrollbar">
                    <div className="container-fluid" style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
                        <div id="two-column-menu"></div>
                        
                        {/* Main Navigation Menu */}
                        <ul className="navbar-nav" id="navbar-nav">

                            {/* View/Upload Contracts */}
                            <li className="nav-item">
                                <Link className="nav-link menu-link" to="/document-list">
                                    <div className={`left-menu ${location.pathname === "/document-list" ? "left-menu-active" : ""}`}>
                                        <CommonIcon iconName="FileText" color="#fff" />{' '}
                                        <span data-key="t-widgets" style={location.pathname === "/document-list" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>View/Upload Contracts</span>
                                    </div>
                                </Link>
                            </li>

                            {/* Data Explorer */}
                            <li className="nav-item">
                                <Link className="nav-link menu-link" to="/data-explorer">
                                    <div className={`left-menu ${location.pathname === "/data-explorer" ? "left-menu-active" : ""}`}>
                                        <CommonIcon iconName="Telescope" color="#fff" />{' '}
                                        <span data-key="t-widgets" style={location.pathname === "/data-explorer" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>Data Explorer</span>
                                    </div>
                                </Link>
                            </li>

                            {/* Agent Exceptions */}
                            <li className="nav-item">
                                <Link className="nav-link menu-link" to="/agent-routed-exceptions">
                                    <div className={`left-menu ${location.pathname === "/agent-routed-exceptions" ? "left-menu-active" : ""}`}>
                                        <CommonIcon iconName="ShieldAlert" color="#fff" />{' '}
                                        <span data-key="t-widgets" style={location.pathname === "/agent-routed-exceptions" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>Agent Exceptions</span>
                                    </div>
                                </Link>
                            </li>

                            {/* Ask Genie AI Assistant */}
                            <li className="nav-item">
                                <Link className="nav-link menu-link" to="/askgenie">
                                    <div className={`left-menu ${location.pathname === "/askgenie" ? "left-menu-active" : ""}`}>
                                        <CommonIcon iconName="WandSparkles" color="#fff" />{' '}
                                        <span data-key="t-widgets" style={location.pathname === "/askgenie" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>Ask Genie</span>
                                    </div>
                                </Link> 
                            </li>

                            {/* Dashboards - Client Type Conditional */}
                           
                            
                            {/* Alerts - Disabled for now */}
                            <li className="nav-item">
                                <a className="nav-link menu-link disabled-link" href="javascript:void(0)">
                                    <div className="left-menu">
                                        <CommonIcon iconName="Bell" color="#fff" />{' '}
                                        <span data-key="t-widgets">Alerts</span>
                                    </div>
                                </a>
                            </li>

                             <li className="nav-item">
                                <a className="nav-link menu-link" onClick={toggleDashboardDropdown} href="javascript:void(0)">
                                    <div className="left-menu">
                                        <CommonIcon iconName="LayoutDashboard" color="#fff" />{' '}
                                        <span data-key="t-widgets">Dashboard</span>
                                        {/* Dropdown arrow indicator */}
                                        <span style={{ marginLeft: "10px" }}>
                                            {isDashboardDropdownOpen ? (
                                                <CommonIcon iconName="ChevronUp" color="#fff" />
                                            ) : (
                                                <CommonIcon iconName="ChevronDown" color="#fff" />
                                            )}
                                        </span>
                                    </div>
                                </a>
                                
                                {/* Dashboard Submenu - Shows when dropdown is open */}
                                {isDashboardDropdownOpen && (
                                    <div className="" id="sidebarLanding">
                                        <ul className="nav nav-sm flex-column">
                                            <li className="nav-item py-0 my-0">
                                                <Link className="nav-link menu-link" to="/royalty-calculator">
                                                    <div className={`left-menu py-1 ${location.pathname === "/royalty-calculator" ? "left-menu-active" : ""}`}>
                                                        <CommonIcon iconName="Calculator" color="#fff" />{' '}
                                                        <span data-key="t-widgets" style={location.pathname === "/royalty-calculator" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>Royalty Calculator</span>
                                                    </div>
                                                </Link>
                                            </li>
                                            <li className="nav-item py-0 my-0">
                                                <Link className="nav-link menu-link" to="/license-expiry-tracker">
                                                    <div className={`left-menu py-1 ${location.pathname === "/license-expiry-tracker" ? "left-menu-active" : ""}`}>
                                                        <CommonIcon iconName="Timer" color="#fff" />{' '}
                                                        <span data-key="t-widgets" style={location.pathname === "/license-expiry-tracker" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>License Expiry Tracker</span>
                                                    </div>
                                                </Link>
                                            </li>
                                            <li className="nav-item py-0 my-0">
                                                <Link className="nav-link menu-link" to="/territory-mapping">
                                                    <div className={`left-menu py-1 ${location.pathname === "/territory-mapping" ? "left-menu-active" : ""}`}>
                                                        <CommonIcon iconName="Map" color="#fff" />{' '}
                                                        <span data-key="t-widgets" style={location.pathname === "/territory-mapping" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>Territory Mapping</span>
                                                    </div>
                                                </Link>
                                            </li>
                                            <li className="nav-item py-0 my-0">
                                                <Link className="nav-link menu-link" to="/brand-ip-portfolio">
                                                    <div className={`left-menu py-1 ${location.pathname === "/brand-ip-portfolio" ? "left-menu-active" : ""}`}>
                                                        <CommonIcon iconName="Briefcase" color="#fff" />{' '}
                                                        <span data-key="t-widgets" style={location.pathname === "/brand-ip-portfolio" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>Brand IP Portfolio</span>
                                                    </div>
                                                </Link>
                                            </li>
                                            <li className="nav-item py-0 my-0">
                                                <Link className="nav-link menu-link" to="/royalty-reporting">
                                                    <div className={`left-menu py-1 ${location.pathname === "/royalty-reporting" ? "left-menu-active" : ""}`}>
                                                        <CommonIcon iconName="BarChart" color="#fff" />{' '}
                                                        <span data-key="t-widgets" style={location.pathname === "/royalty-reporting" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>Royalty Reporting</span>
                                                    </div>
                                                </Link>
                                            </li>
                                            <li className="nav-item py-0 my-0">
                                                <Link className="nav-link menu-link" to="/financial-forecasting">
                                                    <div className={`left-menu py-1 ${location.pathname === "/financial-forecasting" ? "left-menu-active" : ""}`}>
                                                        <CommonIcon iconName="TrendingUp" color="#fff" />{' '}
                                                        <span data-key="t-widgets" style={location.pathname === "/financial-forecasting" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>Financial Forecasting</span>
                                                    </div>
                                                </Link>
                                            </li>
                                            <li className="nav-item py-0 my-0">
                                                <Link className="nav-link menu-link" to="/licensed-product-catalog">
                                                    <div className={`left-menu py-1 ${location.pathname === "/licensed-product-catalog" ? "left-menu-active" : ""}`}>
                                                        <CommonIcon iconName="BookOpen" color="#fff" />{' '}
                                                        <span data-key="t-widgets" style={location.pathname === "/licensed-product-catalog" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>Licensed Product Catalog</span>
                                                    </div>
                                                </Link>
                                            </li>
                                            <li className="nav-item py-0 my-0">
                                                <Link className="nav-link menu-link" to="/compliance-monitoring">
                                                    <div className={`left-menu py-1 ${location.pathname === "/compliance-monitoring" ? "left-menu-active" : ""}`}>
                                                        <CommonIcon iconName="ShieldCheck" color="#fff" />{' '}
                                                        <span data-key="t-widgets" style={location.pathname === "/compliance-monitoring" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>Compliance Monitoring</span>
                                                    </div>
                                                </Link>
                                            </li>
                                            <li className="nav-item py-0 my-0">
                                                <Link className="nav-link menu-link" to="/contract-comparison">
                                                    <div className={`left-menu py-1 ${location.pathname === "/contract-comparison" ? "left-menu-active" : ""}`}>
                                                        <CommonIcon iconName="Scale" color="#fff" />{' '}
                                                        <span data-key="t-widgets" style={location.pathname === "/contract-comparison" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>Contract Comparison</span>
                                                    </div>
                                                </Link>
                                            </li>
                                            <li className="nav-item py-0 my-0">
                                                <Link className="nav-link menu-link" to="/audit-trail">
                                                    <div className={`left-menu py-1 ${location.pathname === "/audit-trail" ? "left-menu-active" : ""}`}>
                                                        <CommonIcon iconName="ClipboardList" color="#fff" />{' '}
                                                        <span data-key="t-widgets" style={location.pathname === "/audit-trail" ? { fontWeight: 'bold', color: '#ffd700' } : {}}>Audit Trail</span>
                                                    </div>
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </li>

                            {/* Admin Dropdown Menu */}
                            <li className="nav-item">
                                <a className="nav-link menu-link" onClick={toggleAdminDropdown} href="javascript:void(0)">
                                    <div className="left-menu">
                                        <CommonIcon iconName="Settings" color="#fff" />{' '}
                                        <span data-key="t-widgets">Admin</span>
                                        {/* Dropdown arrow indicator */}
                                        <span style={{ marginLeft: "10px" }}>
                                            {isAdminDropdownOpen ? (
                                                <CommonIcon iconName="ChevronUp" color="#fff" />
                                            ) : (
                                                <CommonIcon iconName="ChevronDown" color="#fff" />
                                            )}
                                        </span>
                                    </div>
                                </a>
                                
                                {/* Admin Submenu - Shows when dropdown is open */}
                                {isAdminDropdownOpen && (
                                    <div className="" id="sidebarLanding">
                                        <ul className="nav nav-sm flex-column">
                                            {/* Generic Client Menu Items */}
                                            <>     
                                            <li className="nav-item">
                                                <Link className="nav-link menu-link" to="/taxonomy-management">
                                                    <div style={{ fontSize: "11px" }} className={`left-menu ${location.pathname === "/taxonomy-management" ? "left-menu-active" : ""}`}>
                                                        <CommonIcon iconName="Tag" color="#fff" />{' '}
                                                        <span data-key="t-widgets">Taxonomy Management</span>
                                                    </div>
                                                </Link>
                                            </li>
                                            <li className="nav-item">
                                                <Link className="nav-link menu-link" to="/agent-confidence-settings">
                                                    <div style={{ fontSize: "11px" }} className={`left-menu ${location.pathname === "/agent-confidence-settings" ? "left-menu-active" : ""}`}>
                                                        <CommonIcon iconName="FileSliders" color="#fff" />{' '}
                                                        <span data-key="t-widgets">Confidence Settings</span>
                                                    </div>
                                                </Link>
                                            </li>
                                            </> 
                                        </ul>
                                    </div>
                                )}
                            </li>

                            {/* About Menu Item - Opens About Modal */}
                            <li className="nav-item">
                                <a className="nav-link menu-link" href="#" onClick={toggleAboutDialog}>
                                    <div className="left-menu">
                                        <CommonIcon iconName="Info" color="#fff" />{' '}
                                        <span data-key="t-widgets">About</span>
                                    </div>
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* User Information and Logout Section */}
                    <div className="login-user-info d-none">
                        <div className="user-details">
                            <div>{logged_user_name}</div>
                            <div>{localStorage.getItem("organization")}</div>
                        </div>
                        {/* Logout Button */}
                        <div className="logout">
                            <a href="javascript:void(0)" onClick={logout}>
                                <span>
                                    <CommonIcon iconName="LogOut" color="#fff" />
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
                
                {/* Sidebar Background Decoration */}
                <div className="sidebar-background"></div>
            </div>
        </>
    );
};


  

// export const RenderMenuLeft = () => {
//     const { user,isLoggedIn, logout } = AuthData();
//     const [isMenuSmall, setIsMenuSmall] = useState(false); // Tracks menu size
   
//     const toggleMenu = () => {
//         const htmlElement = document.documentElement; // Refers to the <html> tag
//         const currentMenuStatus = htmlElement.getAttribute('data-sidebar-size');
//         const menuStatus = currentMenuStatus === 'sm' ? 'lg' : 'sm';
//         htmlElement.setAttribute('data-sidebar-size', menuStatus);
//         setIsMenuSmall(!isMenuSmall); // Update the state
//     };

//   // Function to refresh the auth token
//   const refreshAuthToken = async () => {
//     let apiUrl = API_SERVER_URL+ 'refresh-access-token/'
//     let method = "POST"
//     try {
//         const result = await apiCall(method,apiUrl);
//         if(result!==undefined){
//         if (result.code === API_SUCCESS_RESPONSE_CODE) {
//             let jsondata = localStorage.getItem("jsondata")
//             if(jsondata!==undefined && jsondata!== null){
//                 const jsonData = JSON.parse(jsondata);
//                 if(result.data.access_token!==undefined){
//                     jsonData.accessToken =result.data.access_token
//                     localStorage.setItem("jsondata",JSON.stringify(jsonData))
//                  }
//             }
//         } else {
//           trackError("The API response is invalid:<br/>URL:"+apiUrl+"<br/>Method:"+method+"<br>", JSON.stringify(result))
//         }
//         }
//     } catch (error) {
//       console.error("Error refreshing token:", error);
//     }
//   };
//   refreshAuthToken();
//   useEffect(() => {
//     // Call the token refresh function immediately when the component mounts
//     refreshAuthToken();

//     // Set an interval to refresh the token every 15 minutes (900,000 milliseconds)
//     //const interval = setInterval(refreshAuthToken, 100);

//     // Clean up the interval when the component unmounts
//     //return () => clearInterval(interval);
//   }, []); // Empty dependency array ensures this effect runs once on mount


// const logged_user_name = getUsername();

